import React, { useEffect, useRef, useState } from 'react';
import styles from '../styles/HeroVideo.module.css';

/**
 * HeroVideo - Background video component with overlay
 * Plays video once, triggers content fade-in on completion, then loops
 * @param {string} videoUrl - Optional custom video URL (defaults to Firebase storage)
 * @param {Function} onVideoReady - Callback fired after first video play completes
 * @param {boolean} contentVisible - Controls overlay opacity for darkening effect
 * @param {boolean} skipVideo - If true, skips the initial play and immediately shows content with looping video
 */
const HeroVideo = ({ videoUrl, onVideoReady, contentVisible = false, skipVideo = false }) => {
  const videoRef = useRef(null);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(skipVideo);
  const [contentShown, setContentShown] = useState(skipVideo);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // If skipVideo is true, enable looping immediately and show content
    if (skipVideo) {
      video.loop = true;
      video.muted = true;
      video.play().catch((error) => {
        console.error('Video autoplay failed:', error);
      });
      // Content is already shown via initial state, no need to call onVideoReady
      return;
    }

    const playVideo = () => {
      video.muted = true;
      video.play().catch((error) => {
        console.error('Video autoplay failed:', error);
        // If video fails to play, show content immediately as fallback
        if (!contentShown && onVideoReady) {
          onVideoReady();
          setContentShown(true);
        }
      });
    };

    const handleVideoEnd = () => {
      if (!hasPlayedOnce) {
        setHasPlayedOnce(true);
        setContentShown(true);
        // Trigger content display callback
        if (onVideoReady) {
          onVideoReady();
        }
        // Enable looping after first play completes
        video.loop = true;
      }
    };

    const handleVideoError = () => {
      console.error('Video failed to load, showing content as fallback');
      // If video fails to load, show content immediately as fallback
      if (!contentShown && onVideoReady) {
        onVideoReady();
        setContentShown(true);
      }
    };

    // Start playing when video can play
    video.addEventListener('canplay', playVideo);
    // Show content when video ends (first time only)
    video.addEventListener('ended', handleVideoEnd);
    // Fallback: show content if video fails to load
    video.addEventListener('error', handleVideoError);

    // Attempt initial play
    playVideo();

    return () => {
      video.removeEventListener('canplay', playVideo);
      video.removeEventListener('ended', handleVideoEnd);
      video.removeEventListener('error', handleVideoError);
    };
  }, [onVideoReady, hasPlayedOnce, contentShown, skipVideo]);

  return (
    <div
      className={`${styles.hero} ${
        contentVisible ? styles.contentVisible : ''
      }`}
    >
      <video
        ref={videoRef}
        className={styles.video}
        autoPlay
        muted
        playsInline
        preload="auto"
      >
        <source
          src={
            videoUrl ||
            'https://firebasestorage.googleapis.com/v0/b/yourgestureapp.appspot.com/o/SmartSend_Intro_Video.mp4?alt=media&token=a85a382c-2979-44e8-8828-264657b1bb49'
          }
          type="video/mp4"
        />
      </video>
      <div className={styles.overlay} />
    </div>
  );
};

export default HeroVideo;
